import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { examComponentsAPI } from '../../services/api';
import { useData } from '../../context/DataContext';
import Modal from '../../components/Modal';
import { getStudentCode } from '../../utils/studentCode';
import './ExamComponentUploads.css';

const NO_COHORT = '__geen_cohort__';

const toDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const examTypeLabel = (value) => {
  if (value === 'beroepsproduct') return 'Beroepsproduct';
  if (value === 'adviesrapport') return 'Adviesrapport';
  if (value === 'schriftelijke-opdracht') return 'Schriftelijke opdracht';
  return value || '-';
};

const reviewStatusLabel = (value) => (value === 'reviewed' ? 'Beoordeeld' : 'Open');

const idOf = (value) => String(value?.id || value?._id || value || '');

const courseLabel = (course) => (course?.code ? `${course.code} - ${course.name}` : (course?.name || '-'));

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'exam-inzendingen.zip';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const ExamComponentUploads = () => {
  const { data } = useData();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState('');
  const [downloadingKey, setDownloadingKey] = useState('');
  const [error, setError] = useState('');
  const [okMessage, setOkMessage] = useState('');

  const [filterStudy, setFilterStudy] = useState('');
  const [filterCourse, setFilterCourse] = useState('');
  const [filterCohort, setFilterCohort] = useState('');
  const [filterExamType, setFilterExamType] = useState('');
  const [filterReviewStatus, setFilterReviewStatus] = useState('');
  const [search, setSearch] = useState('');
  const [expandedCohorts, setExpandedCohorts] = useState({});

  const [selectedId, setSelectedId] = useState(null);
  const [reviewForm, setReviewForm] = useState({ score: '', feedback: '', reviewStatus: 'pending' });

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await examComponentsAPI.getAdminSubmissions({
        ...(filterCourse ? { courseId: filterCourse } : {}),
        ...(filterExamType ? { examType: filterExamType } : {}),
        ...(filterReviewStatus ? { reviewStatus: filterReviewStatus } : {})
      });
      setItems(response.items || []);
    } catch (loadError) {
      setError(loadError.message || 'Laden van examenuploads mislukt');
    } finally {
      setLoading(false);
    }
  }, [filterCourse, filterExamType, filterReviewStatus]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const studentsById = useMemo(() => {
    const map = new Map();
    (data.students || []).forEach((student) => map.set(idOf(student), student));
    return map;
  }, [data.students]);

  const cohortsById = useMemo(() => {
    const map = new Map();
    (data.cohorts || []).forEach((cohort) => map.set(idOf(cohort), cohort));
    return map;
  }, [data.cohorts]);

  // Vak -> studie via de programmastructuur.
  const studyByCourse = useMemo(() => {
    const map = new Map();
    (data.studies || []).forEach((study) => {
      (study.studyYears || []).forEach((year) => {
        (year.modules || []).forEach((module) => {
          (module.courses || []).forEach((course) => {
            if (!map.has(idOf(course))) map.set(idOf(course), { id: idOf(study), name: study.name });
          });
        });
      });
    });
    return map;
  }, [data.studies]);

  // Verrijkte inzendingen met cohort, studentcode en studie.
  const rows = useMemo(() => (items || []).map((item) => {
    const student = studentsById.get(idOf(item.student));
    const cohortId = idOf(student?.cohort) || NO_COHORT;
    const cohort = cohortsById.get(cohortId) || student?.cohort || null;
    const study = studyByCourse.get(idOf(item.course)) || null;
    return {
      item,
      studentName: student?.name || item.student?.name || '-',
      studentCode: getStudentCode(student),
      email: item.student?.email || student?.email || '',
      cohortId,
      cohortName: cohortId === NO_COHORT ? 'Zonder cohort' : (cohort?.name || cohort?.code || 'Onbekend cohort'),
      cohortLocation: cohort?.location || '',
      studyId: study?.id || '',
      studyName: study?.name || ''
    };
  }), [items, studentsById, cohortsById, studyByCourse]);

  const studyOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => { if (row.studyId) map.set(row.studyId, row.studyName); });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1], 'nl'));
  }, [rows]);

  const courseOptions = useMemo(() => {
    const map = new Map();
    rows
      .filter((row) => !filterStudy || row.studyId === filterStudy)
      .forEach((row) => {
        const id = idOf(row.item.course);
        if (id && !map.has(id)) map.set(id, courseLabel(row.item.course));
      });
    // Gekozen vak blijft in de lijst, ook als de server alleen dat vak teruggeeft.
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1], 'nl'));
  }, [rows, filterStudy]);

  const cohortOptions = useMemo(() => {
    const map = new Map();
    rows
      .filter((row) => !filterStudy || row.studyId === filterStudy)
      .forEach((row) => map.set(row.cohortId, row.cohortName));
    return Array.from(map.entries()).sort((a, b) => {
      if (a[0] === NO_COHORT) return 1;
      if (b[0] === NO_COHORT) return -1;
      return a[1].localeCompare(b[1], 'nl', { numeric: true });
    });
  }, [rows, filterStudy]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (filterStudy && row.studyId !== filterStudy) return false;
      if (filterCohort && row.cohortId !== filterCohort) return false;
      if (query && !`${row.studentName} ${row.studentCode} ${row.email} ${courseLabel(row.item.course)}`.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [rows, filterStudy, filterCohort, search]);

  const groups = useMemo(() => {
    const map = new Map();
    filteredRows.forEach((row) => {
      if (!map.has(row.cohortId)) {
        map.set(row.cohortId, { cohortId: row.cohortId, cohortName: row.cohortName, location: row.cohortLocation, rows: [] });
      }
      map.get(row.cohortId).rows.push(row);
    });
    return Array.from(map.values())
      .map((group) => ({
        ...group,
        open: group.rows.filter((row) => row.item.reviewStatus !== 'reviewed').length,
        rows: group.rows.sort((a, b) => (
          a.studentName.localeCompare(b.studentName, 'nl')
          || courseLabel(a.item.course).localeCompare(courseLabel(b.item.course), 'nl')
        ))
      }))
      .sort((a, b) => {
        if (a.cohortId === NO_COHORT) return 1;
        if (b.cohortId === NO_COHORT) return -1;
        return a.cohortName.localeCompare(b.cohortName, 'nl', { numeric: true });
      });
  }, [filteredRows]);

  const totals = useMemo(() => ({
    total: filteredRows.length,
    open: filteredRows.filter((row) => row.item.reviewStatus !== 'reviewed').length
  }), [filteredRows]);

  const selectedRow = rows.find((row) => String(row.item.id) === String(selectedId)) || null;

  const openDetails = (row) => {
    setSelectedId(row.item.id);
    setReviewForm({
      score: row.item.score ?? '',
      feedback: row.item.feedback || '',
      reviewStatus: row.item.reviewStatus || 'pending'
    });
  };

  const submitReview = async () => {
    if (!selectedRow) return;
    const key = String(selectedRow.item.id);
    setSavingId(key);
    setError('');
    setOkMessage('');
    try {
      await examComponentsAPI.reviewSubmission(key, {
        score: reviewForm.score === '' ? null : Number(reviewForm.score),
        feedback: reviewForm.feedback || '',
        reviewStatus: reviewForm.reviewStatus || 'pending'
      });
      setOkMessage('Beoordeling opgeslagen');
      await loadItems();
    } catch (saveError) {
      setError(saveError.message || 'Opslaan beoordeling mislukt');
    } finally {
      setSavingId('');
    }
  };

  const downloadMomentZip = async (item) => {
    if (!item?.course?.id || !item?.scheduleEvent?.id || !item?.examType) {
      setError('Onvoldoende gegevens om zip te downloaden.');
      return;
    }
    const key = `${item.course.id}::${item.scheduleEvent.id}::${item.examType}`;
    setDownloadingKey(key);
    setError('');
    setOkMessage('');
    try {
      const { blob, filename } = await examComponentsAPI.downloadAdminSubmissionsZip({
        courseId: item.course.id,
        scheduleEventId: item.scheduleEvent.id,
        examType: item.examType
      });
      downloadBlob(blob, filename || 'exam-inzendingen.zip');
      setOkMessage('ZIP met alle inzendingen van dit examenmoment is gedownload.');
    } catch (downloadError) {
      setError(downloadError.message || 'Downloaden van zip mislukt');
    } finally {
      setDownloadingKey('');
    }
  };

  const toggleCohort = (cohortId) => setExpandedCohorts((prev) => ({ ...prev, [cohortId]: !prev[cohortId] }));
  const setAll = (expanded) => setExpandedCohorts(Object.fromEntries(groups.map((group) => [group.cohortId, expanded])));

  const resetFilters = () => {
    setFilterStudy('');
    setFilterCourse('');
    setFilterCohort('');
    setFilterExamType('');
    setFilterReviewStatus('');
    setSearch('');
  };

  return (
    <div className="exam-uploads-page">
      <div className="eu-header">
        <div>
          <h1>Examenuploads</h1>
          <p className="eu-muted">Inzendingen voor beroepsproduct, adviesrapport en schriftelijke opdracht, gegroepeerd per cohort. Open een inzending om te bekijken en te beoordelen.</p>
        </div>
        <div className="eu-stats">
          <span><strong>{totals.total}</strong> inzendingen</span>
          <span className="eu-open"><strong>{totals.open}</strong> open</span>
        </div>
      </div>

      {error && <p className="message error">{error}</p>}
      {okMessage && <p className="message ok">{okMessage}</p>}

      <div className="eu-filters">
        <label>
          <span>Zoeken</span>
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Naam, studentcode, e-mail of vak…" />
        </label>
        <label>
          <span>Studie</span>
          <select value={filterStudy} onChange={(e) => { setFilterStudy(e.target.value); setFilterCourse(''); setFilterCohort(''); }}>
            <option value="">Alle studies</option>
            {studyOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        </label>
        <label>
          <span>Vak</span>
          <select value={filterCourse} onChange={(e) => setFilterCourse(e.target.value)}>
            <option value="">Alle vakken</option>
            {courseOptions.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
        </label>
        <label>
          <span>Cohort</span>
          <select value={filterCohort} onChange={(e) => setFilterCohort(e.target.value)}>
            <option value="">Alle cohorten</option>
            {cohortOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        </label>
        <label>
          <span>Examenonderdeel</span>
          <select value={filterExamType} onChange={(e) => setFilterExamType(e.target.value)}>
            <option value="">Alles</option>
            <option value="beroepsproduct">Beroepsproduct</option>
            <option value="adviesrapport">Adviesrapport</option>
            <option value="schriftelijke-opdracht">Schriftelijke opdracht</option>
          </select>
        </label>
        <label>
          <span>Status</span>
          <select value={filterReviewStatus} onChange={(e) => setFilterReviewStatus(e.target.value)}>
            <option value="">Alles</option>
            <option value="pending">Open</option>
            <option value="reviewed">Beoordeeld</option>
          </select>
        </label>
        <div className="eu-filter-actions">
          <button type="button" className="btn-secondary" onClick={resetFilters}>Reset</button>
        </div>
      </div>

      {groups.length > 0 && (
        <div className="eu-expand">
          <button type="button" className="eu-link" onClick={() => setAll(true)}>Alles uitklappen</button>
          <span aria-hidden="true">·</span>
          <button type="button" className="eu-link" onClick={() => setAll(false)}>Alles inklappen</button>
        </div>
      )}

      {loading ? (
        <p>Laden...</p>
      ) : groups.length === 0 ? (
        <p className="eu-muted">Geen examenuploads gevonden voor deze filters.</p>
      ) : (
        <div className="eu-cohort-list">
          {groups.map((group) => {
            const isExpanded = !!expandedCohorts[group.cohortId];
            return (
              <section key={group.cohortId} className="eu-cohort-card">
                <button type="button" className="eu-cohort-toggle" aria-expanded={isExpanded} onClick={() => toggleCohort(group.cohortId)}>
                  <span>
                    <strong>{group.cohortName}</strong>
                    {group.location && <span className="eu-muted"> - {group.location}</span>}
                  </span>
                  <span className="eu-cohort-count">
                    {group.rows.length} inzending(en){group.open ? ` · ${group.open} open` : ''} {isExpanded ? '▲' : '▼'}
                  </span>
                </button>

                {isExpanded && (
                  <div className="eu-table-wrap">
                    <table className="eu-table">
                      <thead>
                        <tr>
                          <th>Student</th>
                          <th>Vak</th>
                          <th>Examenonderdeel</th>
                          <th>Examenmoment</th>
                          <th>Ingediend</th>
                          <th className="num">Score</th>
                          <th>Status</th>
                          <th className="eu-actions-col">Acties</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.rows.map((row) => (
                          <tr key={row.item.id} className={String(selectedId) === String(row.item.id) ? 'selected' : ''}>
                            <td>
                              <div className="eu-student">{row.studentName}</div>
                              <div className="eu-muted">{[row.studentCode, row.email].filter(Boolean).join(' · ')}</div>
                            </td>
                            <td>
                              {courseLabel(row.item.course)}
                              {row.studyName && <div className="eu-muted">{row.studyName}</div>}
                            </td>
                            <td>{examTypeLabel(row.item.examType)}</td>
                            <td>{toDateTime(row.item.scheduleEvent?.startDateTime)}</td>
                            <td>{toDateTime(row.item.submittedAt)}</td>
                            <td className="num">{row.item.score ?? '-'}</td>
                            <td>
                              <span className={`status-pill ${row.item.reviewStatus === 'reviewed' ? 'active' : 'inactive'}`}>
                                {reviewStatusLabel(row.item.reviewStatus)}
                              </span>
                            </td>
                            <td className="eu-actions-col">
                              <div className="eu-row-actions">
                                {row.item.file?.url && (
                                  <a className="btn-secondary" href={row.item.file.url} target="_blank" rel="noreferrer">Bekijken</a>
                                )}
                                <button type="button" className="btn-primary" onClick={() => openDetails(row)}>Beoordelen</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={!!selectedRow}
        onClose={() => setSelectedId(null)}
        title={selectedRow ? `${selectedRow.studentName} – ${examTypeLabel(selectedRow.item.examType)}` : 'Inzending'}
        position="right"
      >
        {selectedRow && (
          <div className="eu-drawer">
            <dl className="eu-facts">
              <div><dt>Student</dt><dd>{selectedRow.studentName}</dd></div>
              <div><dt>Studentcode</dt><dd>{selectedRow.studentCode || '-'}</dd></div>
              <div><dt>E-mail</dt><dd>{selectedRow.email || '-'}</dd></div>
              <div><dt>Cohort</dt><dd>{selectedRow.cohortName}</dd></div>
              <div><dt>Studie</dt><dd>{selectedRow.studyName || '-'}</dd></div>
              <div><dt>Vak</dt><dd>{courseLabel(selectedRow.item.course)}</dd></div>
              <div><dt>Examenmoment</dt><dd>{selectedRow.item.scheduleEvent?.title || '-'} · {toDateTime(selectedRow.item.scheduleEvent?.startDateTime)}</dd></div>
              <div><dt>Ingediend</dt><dd>{toDateTime(selectedRow.item.submittedAt)}</dd></div>
              {selectedRow.item.comments && <div className="eu-facts-full"><dt>Opmerking student</dt><dd>{selectedRow.item.comments}</dd></div>}
            </dl>

            <div className="eu-file-actions">
              {selectedRow.item.file?.url ? (
                <>
                  <a className="btn-secondary" href={selectedRow.item.file.url} target="_blank" rel="noreferrer">Bekijk inzending</a>
                  <a className="btn-secondary" href={selectedRow.item.file.url} download>Download bestand</a>
                </>
              ) : (
                <span className="eu-muted">Geen bestand beschikbaar</span>
              )}
              <button
                type="button"
                className="btn-secondary"
                disabled={downloadingKey === `${selectedRow.item.course?.id}::${selectedRow.item.scheduleEvent?.id}::${selectedRow.item.examType}`}
                onClick={() => downloadMomentZip(selectedRow.item)}
              >
                Alle inzendingen van dit moment (ZIP)
              </button>
            </div>

            <h4 className="eu-drawer-title">Beoordeling</h4>
            <div className="eu-review-form">
              <label>
                <span>Score (0-10)</span>
                <input
                  type="number"
                  min="0"
                  max="10"
                  step="0.1"
                  value={reviewForm.score}
                  onChange={(e) => setReviewForm((prev) => ({ ...prev, score: e.target.value }))}
                />
              </label>
              <label>
                <span>Status</span>
                <select value={reviewForm.reviewStatus} onChange={(e) => setReviewForm((prev) => ({ ...prev, reviewStatus: e.target.value }))}>
                  <option value="pending">Open</option>
                  <option value="reviewed">Beoordeeld</option>
                </select>
              </label>
              <label className="eu-facts-full">
                <span>Feedback</span>
                <textarea rows={4} value={reviewForm.feedback} onChange={(e) => setReviewForm((prev) => ({ ...prev, feedback: e.target.value }))} />
              </label>
            </div>
            {selectedRow.item.reviewedBy?.name && (
              <p className="eu-muted">Laatst beoordeeld door {selectedRow.item.reviewedBy.name} op {toDateTime(selectedRow.item.reviewedAt)}</p>
            )}

            <div className="eu-drawer-actions">
              <button type="button" className="btn-secondary" onClick={() => setSelectedId(null)}>Sluiten</button>
              <button type="button" className="btn-primary" disabled={savingId === String(selectedRow.item.id)} onClick={submitReview}>
                {savingId === String(selectedRow.item.id) ? 'Opslaan…' : 'Beoordeling opslaan'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ExamComponentUploads;
